import type { ButtonHTMLAttributes, ReactNode } from 'react';
import './Button.css';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary';
export type ButtonSize = 'L' | 'M' | 'S' | 'XS';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual emphasis. Default `'primary'`. */
  variant?: ButtonVariant;
  /** Interstate size token. Default `'M'`. */
  size?: ButtonSize;
  /** Optional leading icon. Use `leftIcon` OR `rightIcon`, not both. */
  leftIcon?: ReactNode;
  /** Optional trailing icon. Use `leftIcon` OR `rightIcon`, not both. */
  rightIcon?: ReactNode;
}

/**
 * Interstate DS Button (Figma node 1868:5851). Three variants × four sizes, with
 * hover/active/disabled handled as CSS state (`:hover` / `:active` / native
 * `disabled`) rather than props. All visual tokens resolve through `--genai-btn-*`
 * in tokens.css (light) / theme.css (dark), so it themes automatically.
 */
export function Button({
  variant = 'primary',
  size = 'M',
  leftIcon,
  rightIcon,
  className,
  type,
  children,
  ...rest
}: ButtonProps) {
  const cls = `ids-btn ids-btn--${variant} ids-btn--${size.toLowerCase()}${
    className ? ` ${className}` : ''
  }`;

  return (
    <button type={type ?? 'button'} className={cls} {...rest}>
      {leftIcon && (
        <span className="ids-btn__icon" aria-hidden="true">
          {leftIcon}
        </span>
      )}
      {children}
      {rightIcon && (
        <span className="ids-btn__icon" aria-hidden="true">
          {rightIcon}
        </span>
      )}
    </button>
  );
}
